import { useState } from 'react';
import type { ReportRange } from '@eombor/contracts';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import {
  useBackorderReport,
  useOrgUnitReport,
  usePipelineReport,
  useReceiptReport,
  useRequisitionReport,
  useStockFlow,
  useTopItems,
  useWarehouseReport,
} from '../model/report.queries.js';
import { DEFAULT_RANGE, RangePicker } from './RangePicker.js';
import { AwaitingPickupBlock } from './AwaitingPickupBlock.js';
import { MetricRow } from './MetricRow.js';
import { OrgUnitBlock } from './OrgUnitBlock.js';
import { PipelineBlock } from './PipelineBlock.js';
import { ReceiptTypeBlock } from './ReceiptTypeBlock.js';
import { RequisitionStatusBlock } from './RequisitionStatusBlock.js';
import { StockFlowBlock } from './StockFlowBlock.js';
import { SubdivisionsBlock } from './SubdivisionsBlock.js';
import { TopItemsBlock } from './TopItemsBlock.js';
import { WarehouseBlock } from './WarehouseBlock.js';

/**
 * Boshqaruv panelining TAHLIL bo'limi.
 *
 * Davr SHU YERDA turadi va davrga bog'liq so'rovlarga tarqaladi — har
 * blok o'z davrini saqlasa, ekranda bir vaqtda ikki xil oraliqning
 * raqamlari turishi mumkin edi va ularni solishtirib bo'lmasdi.
 *
 * Bir qism so'rovlar DAVRSIZ va bu ataylab: omborlar kesimi, voronka va
 * bildirgilar JORIY holatni ko'rsatadi — «hozir qayerda nima turibdi»
 * degan savolning o'tgan oy bilan aloqasi yo'q. Ular davr o'zgarganda
 * qayta so'ralmaydi ham.
 *
 * Tartib SAVOLLAR bo'yicha, manba bo'yicha emas:
 *   1. nima kutyapti (voronka) — ish shu yerda;
 *   2. qayerda turibdi (omborlar kesimi);
 *   3. nima bo'ldi (dinamika, tushum turlari, holatlar, mahsulotlar);
 *   4. kim so'radi (korxonalar, bo'linmalar).
 *
 * Har blok O'Z so'rovi bilan yuklanadi: bittasining xatosi qolganini
 * o'chirmaydi va sahifa qismma-qism ochilaveradi.
 */
export interface AnalyticsSectionProps {
  /**
   * Talabnoma sahifasiga o'tish — «olib ketilmagan» oynasidan.
   *
   * Sahifa marshrutlashni BILMAYDI (repo qoidasi): berilmasa oynada
   * tugma umuman chizilmaydi va blok routersiz ham sinaladi.
   */
  onOpenRequisition?: (requisitionId: string) => void;
}

export const AnalyticsSection = ({ onOpenRequisition }: AnalyticsSectionProps = {}) => {
  const [range, setRange] = useState<ReportRange>(DEFAULT_RANGE);
  const { canAnywhere } = usePermission();

  const flow = useStockFlow(range);
  const items = useTopItems(range);
  const requisitions = useRequisitionReport(range);
  const receipts = useReceiptReport(range);
  const orgUnits = useOrgUnitReport(range);

  const warehouses = useWarehouseReport();
  const pipeline = usePipelineReport();
  const backorders = useBackorderReport();

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="text-base font-semibold tracking-tight text-text">{t('report.title')}</h2>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <MetricRow flow={flow} items={items} requisitions={requisitions} />

      {/*
        Har BAND yo butun enni oladi, yo ikkiga TENG bo'linadi.
        Ilgari voronka bilan jadval nomutanosib ikki ustunga qo'yilgan
        edi: voronka uch barobar baland bo'lib, uning yonida jadval
        ostida katta bo'shliq qolardi — ekran buzilgandek ko'rinardi.
        Bo'linish endi bloklarning ICHIDA, ularning o'z mazmuniga qarab.
      */}

      {/*
        «Olib ketilmagan» ko'rsatkichlardan KEYIN, hamma narsadan
        oldin: bu javonda turgan va boshqa hech kimga berib bo'lmaydigan
        mahsulot, ya'ni panelning yagona darhol amal talab qiladigan
        raqami.
      */}
      <AwaitingPickupBlock
        query={pipeline}
        warehouses={warehouses}
        {...(onOpenRequisition === undefined ? {} : { onOpenRequisition })}
      />

      <PipelineBlock query={pipeline} backorders={backorders} />

      {/* Sakkiz ustunli jadval — butun en unga kerak. */}
      <WarehouseBlock query={warehouses} />

      {/*
        Ikki grafik YONMA-YON: ikkovi ham bitta davrning kirimi haqida
        va birga o'qilishi kerak — «qancha keldi» va «qayerdan keldi».
        Biri ikkinchisining ostida turganda ular bog'liq emasdek
        ko'rinardi va ekran ikki barobar cho'zilardi.
      */}
      <div className="grid gap-4 xl:grid-cols-2">
        <StockFlowBlock query={flow} range={range} />
        <ReceiptTypeBlock query={receipts} range={range} />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <RequisitionStatusBlock query={requisitions} />
        <TopItemsBlock query={items} />
      </div>

      <OrgUnitBlock query={orgUnits} />

      {/*
        Bo'linma qoldig'i ALOHIDA ruxsat. Skladchida `report.view` bor, lekin
        `subdivision.stock.read` yo'q — u o'z omborini ko'radi, boshqa
        korxonaning bo'limi ichini emas. Blok jimgina yo'qolmasligi kerak
        edi, lekin bu yerda u umuman CHIZILMAYDI: ochilmaydigan modalga
        olib boradigan ro'yxat "buzuq" deb o'qilardi.
      */}
      {canAnywhere('subdivision.stock.read') ? <SubdivisionsBlock /> : null}
    </section>
  );
};
